import React, { useEffect, useState } from "react";
import { apiRequest } from "../../../service/apiRequest";
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
  Select,
  DatePicker,
} from "antd";
import { createStyles } from "antd-style";
import { HomeOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { theme } from "antd";
import VoucherModel from "./VoucherModel";
import { showSuccess, showError } from "../../../components/errorHandler";
import type { PopconfirmProps, TableColumnsType } from "antd";
import { useCurrentModule } from "../../../hooks/useCurrentModule";
import dayjs from "dayjs";

import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { useSearchParams } from "react-router-dom";
import type { RangePickerProps } from "antd/es/date-picker";

import { saveAs } from "file-saver";

const { RangePicker } = DatePicker;
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const { Option } = Select;

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface Vouchers {
  voucher_id: number;
  voucher_type: string;
  voucher_amount: number;
  voucher_status: number;
  voucher_date: string;
  voucher_reference: string;
  voucher_note: string;
  voucher_account_from: string;
  voucher_account_to: string;
}

const Vouchers: React.FC = () => {
  const [vouchers, setVouchers] = useState<Vouchers[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Vouchers | null>(null);
  const { styles } = useStyle();
  const currentModule = useCurrentModule();

  const [searchParams, setSearchParams] = useSearchParams();

  const voucherType = searchParams.get("voucher_type") || "";
  const voucherDate = searchParams.get("voucher_date") || "";
  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});
  const [filterType, setFilterType] = useState<string>(voucherType);
  const [filterDate, setFilterdate] = useState<string>(voucherDate);
  const [filterVouchers, setFilterVouchers] = useState<Vouchers[]>([]);
  const [pagination, setPagination] = useState<{
    current: number;
    pageSize: number;
    total: number;
  }>({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const handleVoucherTypeChange = (value: string) => {
    const params = new URLSearchParams(searchParams);
    params.set("voucher_type", value);
    if (filterDate) {
      params.set("voucher_date", filterDate);
    }
    setSearchParams(params);
    setFilterType(value);
  };

  const cancel: PopconfirmProps["onCancel"] = (e) => {
    console.log(e);
    showError("Click on No");
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchVoucher = async (
    page = 1,
    limit = 10,
    voucher_type: string | null = null,
    voucher_date: string | null = null,
  ) => {
    try {
      const response = await apiRequest<{
        data: Vouchers[];
        page: number;
        voucher_type: string;
        voucher_date: string;
        limit: number;
        total: number;
      }>({
        method: "get",
        url: `vouchers?page=${page}&limit=${limit}&voucher_type=${filterType}&voucher_date=${filterDate}`,
        data: {
          voucher_type: voucherType,
        },
      });
      console.log("data", response.data);
      setVouchers(response.data);
      setPagination({
        current: response.page,
        pageSize: response.limit,
        total: response.total,
      });
    } catch {
      showError("Failed to fetch vouchers");
    }
  };

  useEffect(() => {
    fetchVoucher(
      pagination.current,
      pagination.pageSize,
      filterType,
      filterDate,
    );
  }, [searchParams]);

  const handleEditClick = (record: Vouchers) => {
    setIsEditMode(true);
    setInitialValues({
      voucher_id: record.voucher_id,
      voucher_type: record.voucher_type,
      voucher_amount: record.voucher_amount,
      voucher_date: record.voucher_date,
      voucher_reference: record.voucher_reference,
      voucher_note: record.voucher_note,
      voucher_account_from: record.voucher_account_from,
      voucher_account_to: record.voucher_account_to,
      voucher_status: record.voucher_status,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchVoucher();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleTableChange: TableProps<Vouchers>["onChange"] = (
    pagination,
    filters,
    sorter,
  ) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  const handleDateRangeChange = (
    dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null,
    dateStrings: [string, string],
  ) => {
    const params = new URLSearchParams(searchParams);

    if (!dates || !dates[0] || !dates[1]) {
      // If date is cleared, remove from filter and URL
      setFilterdate("");
      params.delete("voucher_date");
      setSearchParams(params);
      setFilterVouchers(vouchers); // Optional: reset filtered data
      return;
    }

    const [from, to] = dates;
    const dateRange = dateStrings.join(" - ");
    setFilterdate(dateRange);
    params.set("voucher_date", dateRange);

    if (filterType) {
      params.set("voucher_type", filterType);
    }

    setSearchParams(params);

    const filtered = vouchers.filter((item) => {
      const serviceDate = dayjs(item.voucher_date);
      return (
        serviceDate.isSameOrAfter(from, "day") &&
        serviceDate.isSameOrBefore(to, "day")
      );
    });

    setFilterVouchers(filtered);
  };
  const handleDownloadVoucher = async (voucher_id: number) => {
    try {
      const blob = await apiRequest<Blob>({
        method: "get",
        url: `/download-voucher/${voucher_id}`,
        config: { responseType: "blob" },
      });

      // Create filename (you can customize)
      const fileName = `DownloadVoucher_${voucher_id}.pdf`;

      // Use file-saver to save file
      saveAs(blob, fileName);
    } catch (error) {
      console.error("Failed to download DownloadVoucher", error);
    }
  };

  const columns: TableColumnsType<Vouchers> = [
    {
      title: "Voucher Type",
      dataIndex: "voucher_type",
      key: "voucher_type",
      width: 130,
      filters: Array.from(new Set(vouchers.map((s) => s.voucher_type))).map(
        (type) => ({
          text: type,
          value: type,
        }),
      ),
      filterSearch: true,
      filterMode: "tree",
      filteredValue: filteredInfo.voucher_type || null,
      onFilter: (value, record) =>
        record.voucher_type.includes(value as string),
      sorter: (a, b) => a.voucher_type.localeCompare(b.voucher_type),
      sortOrder:
        sortedInfo.columnKey === "voucher_type" ? sortedInfo.order : null,
      sortDirections: ["ascend", "descend"],
      ellipsis: true,
      render: (_, record) => <div>{record.voucher_type}</div>,
    },
    {
      title: "Amount (₹)",
      dataIndex: "voucher_amount",
      key: "voucher_amount",
      width: 150,
      filters: Array.from(new Set(vouchers.map((s) => s.voucher_amount))).map(
        (amount) => ({
          text: amount,
          value: amount,
        }),
      ),
      filterSearch: true,
      filterMode: "tree",
      filteredValue: filteredInfo.voucher_amount || null,
      ellipsis: true,
      render: (_, record) => <div>{record.voucher_amount}</div>,
    },

    {
      title: "Date",
      dataIndex: "voucher_date",
      key: "voucher_date",
      width: 100,
      filters: Array.from(new Set(vouchers.map((s) => s.voucher_date))).map(
        (date) => ({
          text: date,
          value: date,
        }),
      ),
      filterSearch: true,
      filterMode: "tree",
      filteredValue: filteredInfo.voucher_date || null,
      onFilter: (value, record) =>
        record.voucher_date.includes(value as string),
      sorter: (a, b) => a.voucher_date.localeCompare(b.voucher_date),
      sortOrder:
        sortedInfo.columnKey === "voucher_date" ? sortedInfo.order : null,
      sortDirections: ["ascend", "descend"],
      ellipsis: true,
      render: (_, record) => {
        const [year, month, day] = record.voucher_date.split("-");
        return <div>{`${day}-${month}-${year}`}</div>;
      },
    },
    {
      title: "Reference",
      dataIndex: "voucher_reference",
      key: "voucher_reference",
      width: 200,
      filters: Array.from(
        new Set(vouchers.map((s) => s.voucher_reference)),
      ).map((reference) => ({
        text: reference,
        value: reference,
      })),
      filterSearch: true,
      filterMode: "tree",
      filteredValue: filteredInfo.voucher_reference || null,
      onFilter: (value, record) =>
        record.voucher_reference.includes(value as string),
      sorter: (a, b) => a.voucher_reference.localeCompare(b.voucher_reference),
      sortOrder:
        sortedInfo.columnKey === "voucher_reference" ? sortedInfo.order : null,
      sortDirections: ["ascend", "descend"],
      ellipsis: true,
      render: (_, record) => <div>{record.voucher_reference}</div>,
    },
    {
      title: "Note",
      dataIndex: "voucher_note",
      key: "voucher_note",
      width: 300,
      filters: Array.from(new Set(vouchers.map((s) => s.voucher_note))).map(
        (note) => ({
          text: note,
          value: note,
        }),
      ),
      filterSearch: true,
      filterMode: "tree",
      filteredValue: filteredInfo.voucher_note || null,
      onFilter: (value, record) =>
        record.voucher_note.includes(value as string),
      sorter: (a, b) => a.voucher_note.localeCompare(b.voucher_note),
      sortOrder:
        sortedInfo.columnKey === "voucher_note" ? sortedInfo.order : null,
      sortDirections: ["ascend", "descend"],
      ellipsis: true,
      render: (_, record) => <div>{record.voucher_note}</div>,
    },
    {
      title: "Account From",
      dataIndex: "voucher_account_from",
      key: "voucher_account_from",
      width: 150,
      filters: Array.from(
        new Set(vouchers.map((s) => s.voucher_account_from)),
      ).map((from) => ({
        text: from,
        value: from,
      })),
      filterSearch: true,
      filterMode: "tree",
      filteredValue: filteredInfo.voucher_account_from || null,
      onFilter: (value, record) =>
        record.voucher_account_from.includes(value as string),
      sorter: (a, b) => a.voucher_account_from.localeCompare(b.voucher_note),
      sortOrder:
        sortedInfo.columnKey === "voucher_account_from"
          ? sortedInfo.order
          : null,
      sortDirections: ["ascend", "descend"],
      ellipsis: true,
      render: (_, record) => <div>{record.voucher_account_from}</div>,
    },
    {
      title: "Account To",
      dataIndex: "voucher_account_to",
      key: "voucher_account_to",
      width: 150,
      filters: Array.from(
        new Set(vouchers.map((s) => s.voucher_account_to)),
      ).map((to) => ({
        text: to,
        value: to,
      })),
      filterSearch: true,
      filterMode: "tree",
      filteredValue: filteredInfo.voucher_account_to || null,
      onFilter: (value, record) =>
        record.voucher_account_to.includes(value as string),
      sorter: (a, b) =>
        a.voucher_account_to.localeCompare(b.voucher_account_to),
      sortOrder:
        sortedInfo.columnKey === "voucher_account_to" ? sortedInfo.order : null,
      sortDirections: ["ascend", "descend"],
      ellipsis: true,
      render: (_, record) => <div>{record.voucher_account_to}</div>,
    },
  ];

  // Conditionally add action column
  if (
    currentModule?.right_edit === "Y" ||
    currentModule?.right_delete === "Y"
  ) {
    columns.push({
      title: "Action",
      width: 200,
      render: (_: any, record: Vouchers) => (
        <Space size="middle">
          {currentModule?.right_edit === "Y" && (
            <>
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
              <Button
                onClick={() => handleDownloadVoucher(record.voucher_id)}
                className="px-8"
              >
                Download
              </Button>
            </>
          )}
        </Space>
      ),
    });
  }

  return (
    <>
      <Space
        style={{
          display: "flex",
          marginBottom: 16,
          alignItems: "center",
          width: "100%",
          justifyContent: "space-between",
        }}
      >
        <Breadcrumb style={{ display: "inline-block", margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Vouchers</Breadcrumb.Item>
        </Breadcrumb>

        <Flex
          gap="small"
          justify="space-between"
          align="center"
          style={{ marginBottom: 16 }}
        >
          <Space direction="vertical" size={12}>
            <RangePicker
              // disabledDate={disabledDate}
              onChange={handleDateRangeChange}
              format="DD-MM-YYYY"
            />
          </Space>
          <Select
            showSearch
            optionFilterProp="label"
            defaultValue={voucherType}
            onChange={handleVoucherTypeChange}
            style={{ width: 200, height: 38 }}
          >
            <Option value="">All</Option>
            <Option value="debit" label="debit">Debit</Option>
            <Option value="credit" label="credit">Credit</Option>
          </Select>

          {currentModule?.right_add === "Y" && (
            <Button type="primary" onClick={showCreateModal}>
              Add Voucher
            </Button>
          )}
        </Flex>
      </Space>

      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Vouchers>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="voucher_id"
          dataSource={vouchers.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize,
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 11 }}
          locale={{ emptyText: "No Voucher found" }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(
                  pagination.current * pagination.pageSize,
                  vouchers.length,
                )}{" "}
                of {vouchers.length}
              </span>
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total}
                showSizeChanger
                pageSizeOptions={["10", "20", "50", "100"]}
                onChange={(page, pageSize) => {
                  setPagination((prev) => ({
                    ...prev,
                    current: page,
                    pageSize,
                  }));
                  fetchVoucher(page, pageSize);
                }}
              />
            </Flex>
          )}
        />
      </div>

      <VoucherModel
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />
    </>
  );
};

export default Vouchers;
